import { useQueries, useQuery } from "@tanstack/react-query";
import { produce } from "immer";

import { GraphQLAPI, GraphQLResult, queries } from "@examspark/api";
import {
  IDSK,
  PerformanceTask,
  QuerySearchArgs,
  SearchableResourceConnectionNew,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";

import { useQueryCheckSharability } from "./sharing";

export const useQueriesGetPerformanceTasks = (data: IDSK[]) => {
  const f = useQueryCheckSharability(data);

  const approvedPairs = f.data ?? [];

  return useQueries({
    queries: data
      .filter((idsk) =>
        approvedPairs.find((r) => r.id === idsk.id && r.sk === idsk.sk),
      )
      .map((d) => getPerformanceTask(d.id, d.sk)),
  });
};

export const useQuerySearchPerformanceTaskUsages = (
  performanceTaskId?: string,
) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    enabled: !!performanceTaskId,
    queryKey: ["performance-task-usages", performanceTaskId],
    queryFn: async () => {
      const result = (await GraphQLAPI.query({
        query: queries.search.search({
          label: "SearchPerformanceTaskUsages",
          fields: `id sk workspaceId bankId name grade subject updatedAt`,
        }),
        variables: {
          authorizationContext,
          filter: {
            referencesIds: {
              eq: performanceTaskId,
            },
            sk: {
              eq: "unit#v0",
            },
          },
        } as QuerySearchArgs,
      })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

      return result.data?.search;
    },
  });
};

export const useQueryGetRubric = (id?: string, sk?: string) => {
  return useQuery({
    queryKey: ["resource", id, sk],
    queryFn: async () => {
      const result = (
        (await GraphQLAPI.query({
          query: queries.resource.getResource({
            fields: `id sk name workspaceId bankId createdAt updatedAt content { rubric }`,
          }),
          variables: {
            id: id,
            sk: sk,
          },
        })) as any
      ).data.getResource;

      const next = produce(result, (draft: any) => {
        if (draft.content.rubric) {
          draft.content.rubric = JSON.parse(draft.content.rubric);
        }
      });

      return next as any;
    },
  });
};

const getPerformanceTask = (id?: string, sk = "resource#v0") => ({
  queryKey: ["performance-task", id, sk],
  enabled: !!id && !!sk,
  queryFn: async () => {
    const result = (
      (await GraphQLAPI.query({
        query: queries.media.getPerformanceTask({
          fields: `
              id
              sk
              name
              workspaceId
              bankId
              createdAt
              updatedAt
              objectives {
                id
                objective {
                  id
                  sk
                  content
                  notes
                  abbreviation
                  parentId
                  scope
                  sequence
                }
              }
              content {
                body
              }
              rubrics {
                rubric {
                  id
                  sk
                  workspaceId
                  content {
                    rubric
                  }
                }
              }
              resourcesIds {
                id
                sk
              }
            `,
        }),
        variables: {
          input: {
            id: id,
            sk: sk,
          },
        },
      })) as GraphQLResult<{ getPerformanceTask: PerformanceTask }>
    ).data?.getPerformanceTask;

    return result;
  },
});

export const useQueryGetPerformanceTask = (id?: string, sk?: string) => {
  return useQuery(getPerformanceTask(id, sk));
};
